import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import Logo from "@/components/Logo";
import LanguageToggle from "@/components/LanguageToggle";
import GoogleAuthButton from "@/components/GoogleAuthButton";
import { Loader2 } from "lucide-react";

export default function Login() {
  const { t } = useTranslation();
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const nextUrl = new URLSearchParams(location.search).get("next") || "/dashboard";
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const u = await login(form.email, form.password);
      toast.success(t("auth.welcome"));
      if (u.role === "admin") navigate("/admin/orders");
      else navigate(nextUrl);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#fdfbf7] px-6 py-16 relative overflow-hidden">
      <div className="absolute top-6 left-6"><Logo size="md" /></div>
      <div className="absolute top-6 right-6"><LanguageToggle /></div>

      <div className="w-full max-w-md" data-testid="login-page">
        <div className="text-center">
          <div className="text-xs uppercase tracking-[0.3em] text-[#00868e] font-semibold">
            sign in
          </div>
          <h1 className="mt-3 font-serif-cn text-3xl md:text-4xl text-[#003645] font-semibold">
            {t("auth.welcome")}
          </h1>
        </div>

        <form onSubmit={submit} className="mt-10 space-y-4 bg-white border border-[#e5ebeb] rounded-lg p-8">
          <GoogleAuthButton dataTestId="login-google-btn" />
          <div className="relative flex items-center py-1">
            <div className="flex-grow border-t border-[#e5ebeb]" />
            <span className="mx-3 text-[11px] text-[#4a6e77] uppercase tracking-widest">{t("auth.or")}</span>
            <div className="flex-grow border-t border-[#e5ebeb]" />
          </div>
          <div>
            <label className="block text-xs text-[#4a6e77] mb-1.5">{t("auth.email")}</label>
            <input
              type="email"
              required
              className="w-full px-4 py-2.5 rounded-md border border-[#e5ebeb] focus:border-[#00868e] outline-none transition-colors duration-200"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              data-testid="login-email-input"
            />
          </div>
          <div>
            <label className="block text-xs text-[#4a6e77] mb-1.5">{t("auth.password")}</label>
            <input
              type="password"
              required
              className="w-full px-4 py-2.5 rounded-md border border-[#e5ebeb] focus:border-[#00868e] outline-none transition-colors duration-200"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              data-testid="login-password-input"
            />
          </div>
          {err && (
            <div className="text-sm text-red-600" data-testid="login-error">
              {err}
            </div>
          )}
          <button
            type="submit"
            disabled={loading}
            className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-[#005870] text-white font-medium hover:bg-[#003645] transition-colors duration-200 disabled:opacity-60"
            data-testid="login-submit-btn"
          >
            {loading && <Loader2 size={16} className="animate-spin" />}
            {t("auth.login")}
          </button>
          <div className="text-center text-sm text-[#4a6e77]">
            {t("auth.noAccount")}{" "}
            <Link to="/register" className="text-[#005870] hover:text-[#00868e] font-medium">
              {t("auth.registerHere")}
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
